import { useState } from "react";
import { CheckCircle } from "@phosphor-icons/react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { api } from "@/lib/api";
import { GENDERS } from "@/lib/constants";
import { EMAIL_RE } from "@/lib/format";
import { Face } from "@/lib/brand";
import { currentLang } from "@/i18n/translate";
import CountryPicker from "@/components/admin/CountryPicker";
import { botMessage, useBotCheck } from "@/components/site/BotCheck";

// Waitlist source from the landing URL (?ref= or ?utm_source=), sanitized like the backend: [a-z0-9_.:-], 64 chars. Kept for the visit.
const SOURCE_KEY = "cw_waitlist_source";
const clean = (v) => String(v).toLowerCase().replace(/[^a-z0-9_.:-]/g, "").slice(0, 64);
function waitlistSource() {
  const q = new URLSearchParams(window.location.search);
  const ref = clean(q.get("ref") || "");
  const utm = clean(q.get("utm_source") || "");
  const fromUrl = ref ? clean(`ref:${ref}`) : utm ? clean(`utm:${utm}`) : "";
  if (fromUrl) sessionStorage.setItem(SOURCE_KEY, fromUrl);
  return fromUrl || sessionStorage.getItem(SOURCE_KEY) || "website";
}
waitlistSource(); // capture the landing URL's source as soon as the site loads

// Any country's postal code format (same sanity check as the server: 2-10 letters/digits, at least one digit).
// The server still requires 5 digits for the US.
const postalOk = (z) => { const v = z.replace(/[\s-]/g, "").toUpperCase(); return /^[0-9A-Z]{2,10}$/.test(v) && /\d/.test(v); };
const EMPTY = { email: "", country: "", zip: "", city: "", state: "", gender: "" };

function validate({ email, country, zip, gender }) {
  const e = {};
  if (!EMAIL_RE.test(email.trim())) e.email = "Enter a valid email address.";
  if (!country) e.country = "Choose your country.";
  if (!postalOk(zip)) e.zip = "Enter a valid postal code.";
  if (!gender) e.gender = "Choose an option.";
  return e;
}

const SERVER_FIELD = { invalid_zip: "zip", invalid_gender: "gender", validation: "email", sanctioned: "country" };

function TextField({ id, label, value, onChange, error, testId, ...rest }) {
  return (
    <div>
      <Label htmlFor={id} className="text-sm font-medium">{label}</Label>
      <Input id={id} value={value} onChange={(e) => onChange(e.target.value)} aria-invalid={!!error} aria-describedby={error ? `${id}-error` : undefined}
        className="mt-2 h-12 rounded-xl bg-white px-4 text-base" data-testid={testId} {...rest} />
      <FieldError id={`${id}-error`}>{error}</FieldError>
    </div>
  );
}

function FieldError({ id, children }) {
  if (!children) return null;
  return <p id={id} className="cw-error mt-2 text-sm" role="alert" data-testid={id}>{children}</p>;
}

function Done({ result, onReset }) {
  const fresh = result.status === "joined";
  return (
    <div className="cw-rise flex flex-col items-start" data-testid={fresh ? "waitlist-success" : "waitlist-duplicate"} role="status">
      <Face name={fresh ? "wink" : "crush"} className="h-14 w-14" />
      <h3 className="mt-6 text-2xl font-semibold tracking-tight">{fresh ? "You're on the list." : "You're already on the list."}</h3>
      <p className="cw-soft mt-3 leading-7">
        {fresh ? `We'll write to ${result.email} the moment Crushwink opens near you. Nothing else.`
          : `${result.email} was already saved. We've updated your area and details.`}
      </p>
      <button type="button" onClick={onReset} className="cw-brand-deep mt-6 rounded text-sm font-medium underline-offset-4 hover:underline" data-testid="waitlist-reset-button">
        Add another email
      </button>
    </div>
  );
}

export default function WaitlistForm({ footnote, submitLabel }) {
  const [form, setForm] = useState(EMPTY);
  const [errors, setErrors] = useState({});
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const set = (k) => (v) => { setForm((f) => ({ ...f, [k]: v })); setErrors((e) => ({ ...e, [k]: undefined, form: undefined })); };
  const bot = useBotCheck("waitlist");

  async function submit(ev) {
    ev.preventDefault();
    const e = validate(form);
    setErrors(e);
    if (Object.keys(e).length) return;
    setBusy(true);
    try {
      const token = await bot.getToken();
      const res = await api("/waitlist", { auth: false, body: { email: form.email.trim(), zip: form.zip.trim(), gender: form.gender, source: waitlistSource(), country: form.country, city: form.city.trim() || null, state: form.state.trim() || null, language: currentLang(), ...bot.fields(token) } });
      setResult({ email: form.email.trim(), ...res });
    } catch (err) {
      const field = SERVER_FIELD[err.code];
      setErrors(botMessage(err) ? { form: botMessage(err) } : field ? { [field]: err.message } : { form: err.message });
    } finally {
      bot.reset(); // tokens are single-use
      setBusy(false);
    }
  }

  if (result) return <Done result={result} onReset={() => { setResult(null); setForm(EMPTY); }} />;

  return (
    <form onSubmit={submit} noValidate className="relative space-y-5" data-testid="waitlist-form" aria-label="Join the waitlist">
      {bot.honeypot}
      <div>
        <Label htmlFor="wl-email" className="text-sm font-medium">Email</Label>
        <Input id="wl-email" type="email" autoComplete="email" placeholder="you@example.com" value={form.email}
          onChange={(e) => set("email")(e.target.value)} aria-invalid={!!errors.email} aria-describedby="waitlist-email-error"
          className="mt-2 h-12 rounded-xl bg-white px-4 text-base" data-testid="waitlist-email-input" />
        <FieldError id="waitlist-email-error">{errors.email}</FieldError>
      </div>
      <div className="grid gap-5 sm:grid-cols-2">
        <div>
          <Label htmlFor="wl-country" className="text-sm font-medium">Country</Label>
          <CountryPicker id="wl-country" value={form.country} onChange={set("country")} placeholder="Choose" invalid={!!errors.country}
            className="mt-2 h-12 rounded-xl px-4 text-base" testId="waitlist-country-picker" />
          <FieldError id="waitlist-country-error">{errors.country}</FieldError>
        </div>
        <TextField id="waitlist-zip" label="ZIP / postal code" value={form.zip} onChange={set("zip")} error={errors.zip} testId="waitlist-zip-input"
          autoComplete="postal-code" maxLength={12} placeholder="Postal code" />
      </div>
      <div className="grid gap-5 sm:grid-cols-2">
        <TextField id="waitlist-city" label={<>City <span className="cw-muted font-normal">(optional)</span></>} value={form.city} onChange={set("city")} testId="waitlist-city-input" autoComplete="address-level2" maxLength={80} />
        <TextField id="waitlist-state" label={<>State / province <span className="cw-muted font-normal">(optional)</span></>} value={form.state} onChange={set("state")} testId="waitlist-state-input" autoComplete="address-level1" maxLength={80} />
      </div>
      <div className="grid gap-5 sm:grid-cols-2">
        <div>
          <Label htmlFor="wl-gender" className="text-sm font-medium">I am a</Label>
          <Select value={form.gender} onValueChange={set("gender")}>
            <SelectTrigger id="wl-gender" aria-invalid={!!errors.gender} className="mt-2 h-12 rounded-xl bg-white px-4 text-base" data-testid="waitlist-gender-select">
              <SelectValue placeholder="Choose" />
            </SelectTrigger>
            <SelectContent>
              {GENDERS.map((g) => <SelectItem key={g.key} value={g.key} data-testid={`waitlist-gender-option-${g.key}`}>{g.label}</SelectItem>)}
            </SelectContent>
          </Select>
          <FieldError id="waitlist-gender-error">{errors.gender}</FieldError>
        </div>
      </div>
      {bot.slot}
      <FieldError id="waitlist-form-error">{errors.form}</FieldError>
      <Button type="submit" disabled={busy} className="s-btn-primary h-12 w-full rounded-full text-base font-medium shadow-none transition-all cw-ease active:scale-[0.99]" data-testid="waitlist-submit-button">
        {busy ? "Joining…" : submitLabel || "Join the waitlist"}
      </Button>
      <p className="cw-muted flex items-start gap-2 text-xs leading-5" data-testid="waitlist-footnote">
        <CheckCircle size={16} className="mt-px shrink-0" aria-hidden />
        {footnote || "One email when we open near you. No spam."}
      </p>
    </form>
  );
}
